import { tooltipContainer } from '@/utils/portalContainer'
import { CloseButton, Popover, type PopoverProps } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { merge } from 'lodash-es'
import { motion } from 'motion/react'
import { type ReactNode, useEffect } from 'react'
import styles from './Tooltip.module.css'

export interface TooltipProps extends Omit<PopoverProps, 'children'> {
	children: ReactNode
	content: ReactNode
	withArrow?: boolean
	position?: PopoverProps['position']
	disabled?: boolean
	autoHide?: boolean
	autoHideDelay?: number
	withCloseButton?: boolean
	closeButtonLabel?: string
	onCloseButtonClick?: () => void
}

export default function Tooltip({
	children,
	content,
	withArrow = true,
	position = 'top',
	disabled = false,
	autoHide = false,
	autoHideDelay = 5000,
	withCloseButton = false,
	closeButtonLabel = '关闭提示',
	onCloseButtonClick,
	opened,
	onClose,
	...props
}: TooltipProps) {
	useEffect(() => {
		if (autoHide && opened) {
			const timer = setTimeout(() => {
				onClose?.()
			}, autoHideDelay)
			return () => clearTimeout(timer)
		}
	}, [autoHide, autoHideDelay, opened, onClose])

	if (disabled) {
		return <>{children}</>
	}

	return (
		<Popover
			position={position}
			withArrow={withArrow}
			floatingStrategy="fixed"
			opened={opened}
			onClose={onClose}
			portalProps={{
				target: tooltipContainer,
			}}
			unstyled
			{...props}
			styles={merge(
				{
					arrow: {
						backgroundColor: 'var(--bg-jike-yellow)',
					},
				},
				props.styles
			)}
		>
			<Popover.Target>{children}</Popover.Target>
			<Popover.Dropdown className={styles.dropdown}>
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
					{withCloseButton ? (
						<div className={styles.body}>
							<div className={styles.content}>{content}</div>
							<CloseButton
								type="button"
								aria-label={closeButtonLabel}
								className={styles.closeButton}
								variant="transparent"
								icon={<IconX className={styles.closeIcon} size={14} />}
								onClick={() => {
									if (onCloseButtonClick) {
										onCloseButtonClick()
										return
									}
									onClose?.()
								}}
								size={18}
							/>
						</div>
					) : (
						content
					)}
				</motion.div>
			</Popover.Dropdown>
		</Popover>
	)
}
